import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { adminApi, formatApiError } from "../../lib/adminApi";
import ImageUpload from "../../components/ImageUpload";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  number: "05",
  title: "",
  excerpt: "",
  date: new Date().toISOString().slice(0, 10),
  image: "",
  content: "",
};

export default function AdminJournalForm() {
  const { id } = useParams();
  const isEdit = id && id !== "nuevo";
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(isEdit);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await adminApi.get(`/journal/${id}`);
        setForm({
          number: data.number,
          title: data.title,
          excerpt: data.excerpt,
          date: data.date,
          image: data.image,
          content: (data.content || []).join("\n\n"),
        });
      } catch (e) {
        toast.error(formatApiError(e));
        navigate("/admin/cuaderno");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isEdit, navigate]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.image) return toast.error("La imagen es obligatoria.");
    setBusy(true);
    try {
      const payload = {
        number: form.number,
        title: form.title,
        excerpt: form.excerpt,
        date: form.date,
        image: form.image,
        content: form.content
          .split(/\n\s*\n/)
          .map((p) => p.trim())
          .filter(Boolean),
      };
      if (isEdit) {
        await adminApi.put(`/journal/${id}`, payload);
        toast.success("Nota actualizada");
      } else {
        await adminApi.post("/journal", payload);
        toast.success("Nota creada");
      }
      navigate("/admin/cuaderno");
    } catch (ex) {
      toast.error(formatApiError(ex));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="py-20 flex justify-center">
        <Loader2 className="text-[var(--pc-bronze)] animate-spin" />
      </div>
    );
  }

  return (
    <section className="px-10 py-12 max-w-[820px]" data-testid="admin-journal-form">
      <button
        onClick={() => navigate("/admin/cuaderno")}
        className="flex items-center gap-2 text-[var(--pc-text-soft)] hover:text-[var(--pc-text)] mb-6"
        style={{ fontFamily: "Inter", fontWeight: 400, fontSize: 11, letterSpacing: "0.28em", textTransform: "uppercase" }}
        data-testid="back-to-journal"
      >
        <ArrowLeft size={14} /> Volver
      </button>

      <h1
        className="headline-xl text-[var(--pc-text)]"
        style={{ fontSize: "clamp(24px, 2.4vw, 34px)", letterSpacing: "0.16em", lineHeight: 1.2 }}
      >
        {isEdit ? "Editar nota" : "Nueva nota"}
      </h1>
      <div className="h-px w-10 bg-[var(--pc-bronze)] mt-5 opacity-90" />

      <form onSubmit={submit} className="mt-10 space-y-7">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div>
            <label className="admin-label">Número</label>
            <input
              type="text"
              value={form.number}
              onChange={(e) => set({ number: e.target.value })}
              className="admin-input w-full"
              maxLength={4}
              required
              data-testid="journal-number"
            />
          </div>
          <div className="md:col-span-2">
            <label className="admin-label">Fecha (AAAA-MM-DD)</label>
            <input
              type="date"
              value={form.date}
              onChange={(e) => set({ date: e.target.value })}
              className="admin-input w-full"
              required
              data-testid="journal-date"
            />
          </div>
        </div>

        <div>
          <label className="admin-label">Título (usa Enter para salto de línea)</label>
          <textarea
            value={form.title}
            onChange={(e) => set({ title: e.target.value })}
            className="admin-input w-full"
            rows={3}
            required
            data-testid="journal-title"
          />
        </div>

        <div>
          <label className="admin-label">Resumen (excerpt)</label>
          <textarea
            value={form.excerpt}
            onChange={(e) => set({ excerpt: e.target.value })}
            className="admin-input w-full"
            rows={3}
            required
            data-testid="journal-excerpt"
          />
        </div>

        <ImageUpload
          value={form.image}
          onChange={(v) => set({ image: v })}
          label="Imagen principal"
          testId="journal-image"
        />

        <div>
          <label className="admin-label">Contenido (separa párrafos con doble salto de línea)</label>
          <textarea
            value={form.content}
            onChange={(e) => set({ content: e.target.value })}
            className="admin-input w-full"
            rows={14}
            data-testid="journal-content"
            placeholder="Escribe el primer párrafo aquí.&#10;&#10;Deja una línea en blanco para el siguiente párrafo."
          />
        </div>

        <div className="pt-4 flex gap-3">
          <button type="submit" disabled={busy} className="admin-btn-primary" data-testid="save-journal">
            {busy ? "Guardando..." : isEdit ? "Guardar cambios" : "Crear nota"}
          </button>
          <button
            type="button"
            onClick={() => navigate("/admin/cuaderno")}
            className="admin-btn-ghost"
            data-testid="cancel-journal"
          >
            Cancelar
          </button>
        </div>
      </form>
    </section>
  );
}
